import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  User, 
  Shield, 
  Download, 
  Trash2, 
  AlertTriangle, 
  Loader2, 
  LogOut,
  Mail,
  Calendar,
  FileText,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import FloatingNav from '../components/FloatingNav';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const AccountPage = () => {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState(user?.name || '');
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('name', name);
      await api.put('/user/profile', formData);
      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erro ao atualizar perfil.' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const response = await api.get('/user/export');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mapapodal_dados_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Dados exportados com sucesso!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erro ao exportar dados.' });
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await api.delete('/user/delete');
      logout();
      navigate('/');
    } catch (err) {
      setMessage({ type: 'error', text: 'Erro ao excluir conta.' });
      setDeleteLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen pb-28" style={{ background: 'linear-gradient(180deg, #FDF9F3 0%, #F8F5EF 100%)' }}>
      {/* Header */}
      <header
        className="px-4 py-5 flex items-center gap-4 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at top right, #0F766E 0%, #083D3A 60%, #0A0F14 100%)',
        }}
      >
        <div className="absolute inset-0 scanner-grid opacity-25 pointer-events-none" />
        <button 
          onClick={() => navigate(-1)}
          className="relative z-10 p-2 text-white/80 hover:text-cyan-300 transition-colors rounded-xl hover:bg-white/5"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="relative z-10">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-cyan-300">Perfil</p>
          <h1 className="font-bold text-xl text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            Minha Conta
          </h1>
        </div>
      </header>

      <main className="px-4 py-6 max-w-lg mx-auto">
        {/* Messages */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-2xl text-sm animate-fade-in-up ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        {/* Profile Card */}
        <section className="mb-6 animate-fade-in-up">
          <div className="bg-white rounded-3xl p-6 shadow-lg border border-[#E8E4D9]/50">
            {/* User Info Header */}
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-[#E8E4D9]">
              <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center">
                <User className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-xl text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {user?.name}
                </h2>
                <p className="text-[#8E8E93] flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {user?.email}
                </p>
              </div>
            </div>

            {/* Edit Form */}
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-[#48484A] font-medium mb-2 block">Nome</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-14 rounded-xl border-2 border-[#E8E4D9] focus:border-[#2E5C55] focus:ring-0 bg-[#FDFBF7] text-base"
                  data-testid="name-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-[#2E5C55] to-[#3A7068] hover:from-[#234744] hover:to-[#2E5C55] text-white rounded-full font-semibold transition-all"
                data-testid="update-profile-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar alterações'
                )}
              </Button>
            </form>
          </div>
        </section>

        {/* Privacy Section (LGPD) */}
        <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#2E5C55]/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-[#2E5C55]" />
            </div>
            <h2 className="font-bold text-lg text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
              Privacidade e Dados (LGPD)
            </h2>
          </div>

          <div className="space-y-4">
            {/* Export Data */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#E8E4D9]/50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#4DB6AC]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-6 w-6 text-[#4DB6AC]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#1C1C1E] mb-1">Exportar seus dados</h3>
                  <p className="text-sm text-[#8E8E93] mb-4">
                    Baixe uma cópia de todos os seus dados em formato JSON.
                  </p>
                  <Button 
                    onClick={handleExportData}
                    disabled={exportLoading}
                    variant="outline"
                    className="w-full h-12 border-2 border-[#4DB6AC] text-[#4DB6AC] hover:bg-[#4DB6AC] hover:text-white rounded-full font-semibold transition-all"
                    data-testid="export-data-btn"
                  >
                    {exportLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Exportando...
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5 mr-2" />
                        Exportar dados
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Delete Account */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-red-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-600 mb-1">Excluir conta</h3>
                  <p className="text-sm text-[#8E8E93] mb-4">
                    Essa ação é irreversível. Todos os seus dados serão permanentemente excluídos.
                  </p>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="destructive"
                        className="w-full h-12 bg-red-500 hover:bg-red-600 text-white rounded-full font-semibold"
                        data-testid="delete-account-btn"
                      >
                        <Trash2 className="h-5 w-5 mr-2" />
                        Excluir minha conta
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-3xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl" style={{ fontFamily: 'Syne, sans-serif' }}>
                          Tem certeza absoluta?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-[#8E8E93]">
                          Esta ação não pode ser desfeita. Isso excluirá permanentemente sua conta
                          e removerá todos os seus dados, incluindo leituras, assinaturas e informações pessoais.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleDeleteAccount}
                          className="bg-red-500 hover:bg-red-600 rounded-full"
                          data-testid="confirm-delete-btn"
                        >
                          {deleteLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Excluindo...
                            </>
                          ) : (
                            'Sim, excluir minha conta'
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Security Section */}
        <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#D4A373]/10 flex items-center justify-center">
              <Lock className="h-5 w-5 text-[#D4A373]" />
            </div>
            <h2 className="font-bold text-lg text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
              Segurança
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#E8E4D9]/50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-[#1C1C1E] mb-1">Sessão ativa</h3>
                <p className="text-sm text-[#8E8E93]">
                  Encerre sua sessão atual
                </p>
              </div>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="h-12 px-6 border-2 border-[#2E5C55] text-[#2E5C55] hover:bg-[#2E5C55] hover:text-white rounded-full font-semibold transition-all"
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </section>
      </main>
      <FloatingNav />
    </div>
  );
};

export default AccountPage;
